import {
  useRemovePaymentMethodV2Mutation,
  useUpdateServiceActivationsV2Mutation,
} from '../../../../../codegen/vehicles';
import { useCustomIntl } from '../../../../../i18n/i18n';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { useAppDispatch, useAppSelector } from '../../../../../state/hooks';
import { useServiceConfigurationFormContext } from '../serviceConfigurationDialog/ServiceConfigurationForm';
import {
  closeConfigurationDialog,
  closeRemoveConfirmationDialog,
  DialogMode,
  getDialogMode,
  getPaymentPartnerType,
  getRemoveConfirmationDialog,
  getSelectedVehicleId,
  openAddNewServiceCardDialog,
  openConfigurationDialog,
} from '../../../state/onboardingUiSlice';
import { RemoveConfirmationDialogBody } from './RemoveConfirmationDialogBody';
import { RemoveConfirmationDialogFooter } from './RemoveConfirmationDialogFooter';
import { useLoadedVehicles } from '../../../../../state/vehicles/useVehicles';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import { PaymentPartnerType } from '../../../../../codegen/transactions';

export const RemoveConfirmationDialog = () => {
  const dispatch = useAppDispatch();
  const intl = useCustomIntl();
  const dialogMode = useAppSelector(getDialogMode);
  const paymentParnerType = useAppSelector(getPaymentPartnerType);

  const selectedVehicleId = useAppSelector(getSelectedVehicleId);
  const {
    serviceActivationsWithPaymentMethod,
    paymentMethodId,
    open: showDialog,
  } = useAppSelector(getRemoveConfirmationDialog);

  const { removePaymentMethod } = useLoadedVehicles();

  const { getServiceActivations, removePaymentMethodFromServices } =
    useServiceConfigurationFormContext();

  const [
    callUpdateServiceActivationsMutation,
    { isLoading: isUpdateServiceActivationLoading },
  ] = useUpdateServiceActivationsV2Mutation();

  const [
    callRemovePaymentMethodMutation,
    { isLoading: isRemovePaymentMethodLoading },
  ] = useRemovePaymentMethodV2Mutation();

  const openNewServiceCardDialog = (paymentParner: PaymentPartnerType) => {
    dispatch(
      openAddNewServiceCardDialog({
        paymentPartnerType: paymentParner,
      }),
    );
  };

  const openServiceConfigurationDialog = () => {
    dispatch(openConfigurationDialog(dialogMode));
  };

  const closeServiceConfigurationDialog = () => {
    dispatch(closeConfigurationDialog());
  };

  const closeDialog = () => {
    dispatch(closeRemoveConfirmationDialog());
  };

  const handleSubmit = async () => {
    if (!isEmpty(serviceActivationsWithPaymentMethod)) {
      const servicesToDeactivate = map(
        serviceActivationsWithPaymentMethod,
        ({ serviceType }) => serviceType.toString(),
      );
      const serviceActivationUpdate = mapValues(
        getServiceActivations(),
        (serviceConfiguration, serviceType) =>
          servicesToDeactivate.includes(serviceType)
            ? {
                ...serviceConfiguration,
                enabled: false,
                paymentMethodAssignments: [],
              }
            : serviceConfiguration,
      );
      const serviceActivationResult =
        await callUpdateServiceActivationsMutation({
          vehicleId: selectedVehicleId!,
          updateServiceActivationsRequest: {
            serviceActivations: Object.values(serviceActivationUpdate),
          },
        });

      if (!('data' in serviceActivationResult)) {
        return;
      }
    }

    const removePaymentMethodResult = await callRemovePaymentMethodMutation({
      vehicleId: selectedVehicleId!,
      paymentMethodId,
    });

    if (!('data' in removePaymentMethodResult)) {
      return;
    }

    removePaymentMethod(selectedVehicleId!, paymentMethodId);
    updateFormContext();
    closeDialog();
  };

  const updateFormContext = () => {
    const serviceTypes = serviceActivationsWithPaymentMethod.map(
      (s) => s.serviceType,
    );
    removePaymentMethodFromServices(serviceTypes, paymentMethodId);
  };

  return (
    <Dialog
      data-testid={'delete-payment-method-warning-dialog'}
      useOverflow
      bsSize={Dialog.SIZE_SM}
      show={showDialog && !!paymentMethodId}
      onClose={closeDialog}
      title={intl.formatMessage({
        id: 'onboarding.dialog.removeCardConfirmation.title',
      })}
      body={
        <RemoveConfirmationDialogBody
          serviceActivations={serviceActivationsWithPaymentMethod}
        />
      }
      footer={
        <RemoveConfirmationDialogFooter
          isLoading={
            isUpdateServiceActivationLoading || isRemovePaymentMethodLoading
          }
          onClose={() => {
            openServiceConfigurationDialog();
            closeDialog();
          }}
          onSubmit={() => {
            if (dialogMode === DialogMode.FIRST_SERVICE_CARD) {
              closeServiceConfigurationDialog();
              openNewServiceCardDialog(paymentParnerType);
            } else {
              openServiceConfigurationDialog();
            }

            handleSubmit();
          }}
        />
      }
    />
  );
};
